import React, { useEffect, useState, useRef } from 'react';
import './App.scss';
import {
    Alignment,
    Button,
    ButtonVariant,
    Classes,
    Drawer,
    Navbar,
    NonIdealState,
    OverlaysProvider
} from "@blueprintjs/core";
import { BrowserRouter, Outlet, Route, Routes, useLocation, useNavigate } from "react-router";
import { SequenceController } from "./Experiments/SequenceController";
import SessionOnlyRoute from "./SessionOnlyRoute";
import { LoggedOutIndex, RouteLoginHandler, RouteLogoutHandler } from "./LoggedOutIndex";
import { SessionProvider } from "./SessionProvider";
import { LoggedInIndex } from "./LoggedInIndex";
import { AppContainer } from "./Containers";
import { AppToaster } from "./AppToaster";
import { IconNames } from "@blueprintjs/icons";
import { Impressum } from "./Impressum";
import { Manual } from "./Manual";
import { Footer } from "./Footer";
import { Glossar } from "./Glossar";

// Import the LanguageProvider and useLanguage hook
import { LanguageProvider, useLanguage } from "../context/LanguageContext";
import germanFlag from '../assets/de-flag.svg';
import britishFlag from '../assets/gb-flag.svg';

const GlobalLayout = () => {
    const location = useLocation();
    const { state } = location;
    // Add a ref to track if this is the initial render
    const isInitialRender = useRef(true);

    useEffect(() => {
        // Only show toast if it's not the initial render
        if (state?.message && !isInitialRender.current) {
            AppToaster.show({
                message: state.message,
                intent: state.intent,
                timeout: 3000,
                icon: "info-sign",
            });
        }

        // Set initial render to false after first run
        isInitialRender.current = false;

        // eslint-disable-next-line
    }, [state]);

    return (
        <>
            <Outlet />
            <Footer />
        </>
    );
};

const AppLayout = () => {
    const navigate = useNavigate();
    const [isManualOpen, setIsManualOpen] = useState(false);
    const [isGlossarOpen, setIsGlossarOpen] = useState(false);
    // Use the language context
    const { t, language, setLanguage } = useLanguage();

    return (
        <OverlaysProvider>
            <AppContainer>
                <Navbar style={{ marginBottom: "20px" }}>
                    <Navbar.Group align={Alignment.START}>
                        <Navbar.Heading>
                            <Button
                                variant={ButtonVariant.MINIMAL}
                                icon={IconNames.BULLSEYE}
                                text={t('navbar', 'home')}
                                onClick={() => navigate("/")}
                            />
                        </Navbar.Heading>
                        <Navbar.Divider />
                        <Button
                            icon={IconNames.HELP}
                            variant={ButtonVariant.MINIMAL}
                            text={t('navbar', 'manual')}
                            onClick={() => setIsManualOpen(true)}
                        />
                        <Button
                            icon={IconNames.ANNOTATION}
                            variant={ButtonVariant.MINIMAL}
                            text={t('navbar', 'glossary')}
                            onClick={() => setIsGlossarOpen(true)}
                        />
                        <Button
                            icon={IconNames.INFO_SIGN}
                            variant={ButtonVariant.MINIMAL}
                            text={t('navbar', 'imprint')}
                            onClick={() => navigate("/impressum")}
                        />
                    </Navbar.Group>
                    <Navbar.Group align={Alignment.END}>
                        {/* Language Toggle Button with flag icon */}
                        <Button
                            variant={ButtonVariant.MINIMAL}
                            icon={
                                <img
                                    src={language === 'de' ? germanFlag : britishFlag}
                                    alt={t('ui', 'changeLanguage')}
                                    style={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: 2,
                                        verticalAlign: 'middle'
                                    }}
                                />
                            }
                            text=""
                            onClick={() => setLanguage(language === 'de' ? 'en' : 'de')}
                            title={t('ui', 'changeLanguage')}
                            style={{ marginRight: 10, padding: '5px 10px' }}
                        />
                        <Button
                            variant={ButtonVariant.MINIMAL}
                            icon={IconNames.LOG_OUT}
                            text={t('navbar', 'logout')}
                            onClick={() => navigate("/logout")}
                        />
                    </Navbar.Group>
                </Navbar>
                {/* Anleitung Drawer */}
                <Drawer isOpen={isManualOpen} onClose={() => setIsManualOpen(false)}>
                    <div className={Classes.DRAWER_BODY}>
                        <div className={Classes.DIALOG_BODY}>
                            <Button
                                icon={IconNames.ARROW_LEFT}
                                onClick={() => setIsManualOpen(false)}
                                text={t('navbar', 'closeManual')}
                                style={{ marginBottom: 24 }}
                            />
                            <Manual />
                        </div>
                    </div>
                </Drawer>
                {/* Glossar Drawer */}
                <Drawer isOpen={isGlossarOpen} onClose={() => setIsGlossarOpen(false)}>
                    <div className={Classes.DRAWER_BODY}>
                        <div className={Classes.DIALOG_BODY}>
                            <Button
                                icon={IconNames.ARROW_LEFT}
                                onClick={() => setIsGlossarOpen(false)}
                                text={t('navbar', 'closeGlossary')}
                                style={{ marginBottom: 24 }}
                            />
                            <Glossar />
                        </div>
                    </div>
                </Drawer>
                <Outlet />
            </AppContainer>
        </OverlaysProvider>
    );
};

const NotFound = () => {
    // Use language context for translations
    const { t } = useLanguage();

    return (
        <NonIdealState
            icon={IconNames.ERROR}
            title={t('pages', 'notFound', 'title')}
            description={t('pages', 'notFound', 'description')}
            action={
                <Button
                    icon={IconNames.HOME}
                    text={t('pages', 'notFound', 'backToHome')}
                    onClick={() => window.location.href = "/"}
                />
            }
        />
    );
};

// Toast manager component to handle localStorage-based toasts
const ToastManager = () => {
    useEffect(() => {
        // Check for toast message in localStorage
        const showToast = localStorage.getItem('show_toast');
        if (showToast === 'true') {
            const message = localStorage.getItem('logout_message');
            if (message) {
                AppToaster.show({
                    message: message,
                    intent: "success",
                    timeout: 3000,
                    icon: "info-sign",
                });
            }
            // Clear the flags
            localStorage.removeItem('show_toast');
            localStorage.removeItem('logout_message');
        }
    }, []);

    return null;
};

export const App = () => {
    // Add an effect to clear any history state on initial load
    useEffect(() => {
        // Clear any history state when the app first loads
        if (window.history.state && window.history.state.state && window.history.state.state.message) {
            const cleanState = { ...window.history.state };
            if (cleanState.state) {
                delete cleanState.state.message;
                delete cleanState.state.intent;
            }
            window.history.replaceState(cleanState, '');
        }
    }, []);

    return (
        // Wrap the entire app with LanguageProvider
        <LanguageProvider>
            <SessionProvider>
                <BrowserRouter>
                    {/* Add the Toast Manager component */}
                    <ToastManager />
                    <Routes>
                        <Route path="/" element={<GlobalLayout />}>
                            <Route path="*" element={<NotFound />} />
                            <Route index element={<LoggedOutIndex />} />
                            <Route path="/impressum" element={<Impressum />} />
                            <Route path="/login/:userId" element={<RouteLoginHandler />} />
                            <Route path="/logout" element={<RouteLogoutHandler />} />
                            <Route path="app" element={<SessionOnlyRoute />}>
                                <Route element={<AppLayout />}>
                                    <Route index element={<LoggedInIndex />} />
                                    <Route path="sequences/:sequenceId/:screenId?" element={<SequenceController />} />
                                </Route>
                            </Route>
                            <Route path="admin">
                                <Route index element={<h1>Admin</h1>} />
                            </Route>
                        </Route>
                    </Routes>
                </BrowserRouter>
            </SessionProvider>
        </LanguageProvider>
    );
};